import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, ImageOff, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useFavorites } from "@/context/FavoritesContext";

export const ProductCard = ({ product }) => {
  const { add } = useCart();
  const { isFav, toggle } = useFavorites();
  const [err, setErr] = useState(false);
  const out = !product.inStock;
  const fav = isFav(product.sku);

  return (
    <div
      data-testid={`product-card-${product.sku}`}
      className="group relative flex h-full flex-col rounded-lg border border-slate-200 bg-white overflow-hidden hover:shadow-md hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
    >
      {product.discount > 0 && (
        <span className="absolute left-3 top-3 z-10 rounded-full bg-blue-600 px-2.5 py-1 text-xs font-bold text-white">
          -{product.discount}%
        </span>
      )}
      <button
        data-testid={`fav-toggle-${product.sku}`}
        onClick={(e) => { e.preventDefault(); toggle(product); }}
        aria-label="Favori"
        className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur hover:bg-white"
      >
        <Heart className={`h-4 w-4 transition-colors ${fav ? "fill-red-500 text-red-500" : "text-slate-500"}`} />
      </button>
      {out && (
        <span
          data-testid={`out-of-stock-${product.sku}`}
          className="absolute bottom-[132px] left-3 z-10 rounded-full bg-red-600 px-2.5 py-1 text-xs font-bold text-white shadow"
        >
          Rupture de stock
        </span>
      )}

      <Link to={`/produit/${product.sku}`} className="block">
        <div className="relative flex h-52 items-center justify-center overflow-hidden bg-slate-50">
          {product.image && !err ? (
            <img
              src={product.image}
              alt={product.name}
              loading="lazy"
              onError={() => setErr(true)}
              className={`jc-prod-img h-full w-full object-contain p-4 ${out ? "opacity-60 grayscale" : ""}`}
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-slate-300">
              <ImageOff className="h-10 w-10" />
              <span className="text-xs">Image indisponible</span>
            </div>
          )}
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <span className="text-xs font-medium uppercase tracking-wide text-blue-600">{product.category}</span>
        <Link to={`/produit/${product.sku}`}>
          <h3 className="mt-1 line-clamp-2 min-h-[2.6rem] text-sm font-semibold text-slate-900 hover:text-blue-700">
            {product.name}
          </h3>
        </Link>

        <div className="mt-3 flex items-end gap-2">
          <span className="font-head text-xl font-extrabold text-slate-900">{fmtPrice(product.price)}</span>
          {product.oldPrice && (
            <span className="pb-0.5 text-sm text-slate-400 line-through">{fmtPrice(product.oldPrice)}</span>
          )}
        </div>

        <div className="mt-auto pt-4">
          <Button
            data-testid={`add-to-cart-${product.sku}`}
            disabled={out}
            onClick={() => add(product)}
            className="w-full rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400"
          >
            <ShoppingCart className="mr-2 h-4 w-4" />
            {out ? "Indisponible" : "Ajouter"}
          </Button>
        </div>
      </div>
    </div>
  );
};
